// ** React Imports

// ** Next Imports
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

// ** MUI Imports
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { createSvgIcon } from '@mui/material/utils'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Paper from '@mui/material/Paper'

// ** Hook Imports
import useFetch from 'src/hooks/useFetch'
import useSWR from 'swr'
import useSWRImmutable from 'swr/immutable'

// ** Configs
import endPoint from 'src/configs/endpoints/rekrutmenEndPoints'

// ** Custom Components Imports
import CustomDetail from 'src/components/rekrutmen/CustomDetail'
import SpinnerSmall from 'src/components/SpinnerSmall'
import TitikRekrutmen from 'src/components/rekrutmen/detail/CustomTitikRekrutmen'
import TahapanRekrutmen from 'src/components/rekrutmen/detail/CustomTahapanRekrutmen'
import CountdownTimer from 'src/components/rekrutmen/detail/CountdownTimer'
import RekrutmenClosed from 'src/components/rekrutmen/detail/RekrutmenClosed'

import { RekrumentDetailResponse } from 'src/types/rekrutmen-detail'

// ** Third Party Imports
import { format } from 'date-fns'
import { id as IND } from 'date-fns/locale'
import { FacebookShareButton, TwitterShareButton } from 'react-share'

const ReactQuill = dynamic(() => import('react-quill'), { ssr: false })

const FacebookIcon = createSvgIcon(
  <svg viewBox='0 0 310 310'>
    <path d='M81.703 165.106h33.981V305c0 2.762 2.238 5 5 5H178.3c2.762 0 5-2.238 5-5V165.765h39.064c2.54 0 4.677-1.906 4.967-4.429l5.933-51.502c.163-1.417-.286-2.836-1.234-3.899s-2.307-1.673-3.732-1.673h-44.996V71.978c0-9.732 5.24-14.667 15.576-14.667h29.42c2.762 0 5-2.239 5-5V5.037c0-2.762-2.238-5-5-5h-40.545c-.286-.014-.921-.037-1.857-.037-7.035 0-31.488 1.381-50.804 19.151-21.402 19.692-18.427 43.27-17.716 47.358v37.752H81.703c-2.762 0-5 2.238-5 5v50.844c0 2.762 2.238 5.001 5 5.001z' />
  </svg>,
  'FacebookIcon'
)

// const InstagramIcon = createSvgIcon(
//   <svg width='24' height='24'>
//     <path d='M20.947 8.305a6.53 6.53 0 0 0-.419-2.216 4.61 4.61 0 0 0-2.633-2.633 6.606 6.606 0 0 0-2.186-.42L12 2.981l-3.71.055a6.606 6.606 0 0 0-2.185.42 4.607 4.607 0 0 0-2.633 2.633 6.554 6.554 0 0 0-.419 2.185l-.056 3.71.056 3.71c.015.748.156 1.486.419 2.187a4.61 4.61 0 0 0 2.634 2.632 6.584 6.584 0 0 0 2.185.45l3.71.056 3.71-.056a6.59 6.59 0 0 0 2.186-.419 4.615 4.615 0 0 0 2.633-2.633c.263-.7.404-1.438.419-2.187l.056-3.71-.058-3.709zm-8.953 8.297c-2.554 0-4.623-2.069-4.623-4.623s2.069-4.623 4.623-4.623a4.623 4.623 0 0 1 0 9.246zm4.807-8.339a1.077 1.077 0 1 1 1.077-1.078c0 .596-.482 1.078-1.077 1.078z' />
//     <circle cx='11.994' cy='11.979' r='3.003' />
//   </svg>,
//   'InstagramIcon'
// )

const TwitterIcon = createSvgIcon(
  <svg viewBox='0 0 310 310'>
    <path d='M302.973 57.388c-4.87 2.16-9.877 3.983-14.993 5.463 6.057-6.85 10.675-14.91 13.494-23.73.632-1.977-.023-4.141-1.648-5.434s-3.878-1.449-5.665-.39c-10.865 6.444-22.587 11.075-34.878 13.783-12.381-12.098-29.197-18.983-46.581-18.983-36.695 0-66.549 29.853-66.549 66.547 0 2.89.183 5.764.545 8.598C101.163 99.244 58.83 76.863 29.76 41.204c-1.036-1.271-2.632-1.956-4.266-1.825s-3.104 1.05-3.93 2.467c-5.896 10.117-9.013 21.688-9.013 33.461 0 16.035 5.725 31.249 15.838 43.137-3.075-1.065-6.059-2.396-8.907-3.977-1.529-.851-3.395-.838-4.914.033s-2.473 2.473-2.513 4.224c-.007.295-.007.59-.007.889 0 23.935 12.882 45.484 32.577 57.229-1.692-.169-3.383-.414-5.063-.735-1.732-.331-3.513.276-4.681 1.597s-1.557 3.16-1.018 4.84c7.29 22.76 26.059 39.501 48.749 44.605-18.819 11.787-40.34 17.961-62.932 17.961-4.714 0-9.455-.277-14.095-.826-2.305-.274-4.509 1.087-5.294 3.279s.047 4.638 2.008 5.895c29.023 18.609 62.582 28.445 97.047 28.445 67.754 0 110.139-31.95 133.764-58.753 29.46-33.421 46.356-77.658 46.356-121.367 0-1.826-.028-3.67-.084-5.508 11.623-8.757 21.63-19.355 29.773-31.536 1.237-1.85 1.103-4.295-.33-5.998s-3.816-2.255-5.852-1.353z' />
  </svg>,
  'InstagramIcon'
)

const StyledIconButton = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 99,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark
  }
}))

const DetailRekrutmen = () => {
  const router = useRouter()

  const { id } = router.query

  const { FetchWithPayload } = useFetch()

  const payload = {
    url: `${endPoint.rekrutmenDetailEndpoint}/${id}`,
    method: 'GET'
  }

  const { data, isLoading } = useSWR<RekrumentDetailResponse>(
    id ? [`${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`, payload] : null,
    FetchWithPayload
  )

  const payloadView = {
    url: `${endPoint.rekrutmenViewCountEndpoint}/${id}`,
    method: 'GET'
  }

  useSWRImmutable<RekrumentDetailResponse>(
    id ? [`${process.env.NEXT_PUBLIC_BASE_PATH}/api/reqHandler/`, payloadView] : null,
    FetchWithPayload
  )

  // ** Hooks
  const theme = useTheme()
  const hiddenSm = useMediaQuery(theme.breakpoints.down('sm'))

  const modules = {
    toolbar: false
  }

  return (
    <>
      {!isLoading && data && data.data ? (
        <Grid container>
          <Grid item xs={12} sx={{ mb: 20 }}>
            <Box
              sx={{
                py: hiddenSm ? 8 : 48,
                px: hiddenSm ? 4 : 32,
                backgroundColor: theme => theme.myColor?.lightYellow,
                borderRadius: 1
              }}
            >
              <Typography
                variant={hiddenSm ? 'h3' : 'h2'}
                fontWeight={600}
                textAlign={'center'}
                color={theme => theme.palette.primary.main}
                letterSpacing={0}
              >
                {data?.data.judul}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ mb: 20 }}>
            {data?.data.tanggal_akhir_registrasi &&
              new Date(`${data?.data.tanggal_akhir_registrasi}T23:59:59`).getTime() >= Date.now() &&
              new Date(`${data?.data.tanggal_akhir_registrasi}T23:59:59`).getTime() - Date.now() <=
                1000 * 60 * 60 * 24 * 7 && <CountdownTimer endDate={data?.data.tanggal_akhir_registrasi} />}
            {data?.data.tanggal_akhir_registrasi &&
              new Date(`${data?.data.tanggal_akhir_registrasi}T23:59:59`).getTime() < Date.now() && <RekrutmenClosed />}
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12} sm={5} sx={{ px: 2, mb: 12 }}>
                <Grid container rowGap={hiddenSm ? 8 : 12}>
                  {/* <Grid item xs={6}>
                    <CustomDetail captionText='dibuat oleh' boldText='Human Capital Bankaltimtara' />
                  </Grid> */}
                  <Grid item xs={12}>
                    <Typography
                      variant='body1'
                      sx={{ fontWeight: 700, color: theme => theme.myColor?.colorToggle, fontSize: '1.5rem' }}
                    >
                      Waktu Registrasi
                    </Typography>
                    <Box sx={{ display: 'flex' }}>
                      <Typography variant='body1' fontWeight={600} letterSpacing={0}>
                        {data?.data.tanggal_mulai_registrasi != '' && data?.data.tanggal_mulai_registrasi != null
                          ? format(new Date(data?.data.tanggal_mulai_registrasi), 'dd MMMM yyyy', { locale: IND })
                          : ''}
                      </Typography>
                      <Typography variant='body1' fontWeight={600} letterSpacing={0} sx={{ mx: 2 }}>
                        -
                      </Typography>
                      <Typography variant='body1' fontWeight={600} letterSpacing={0}>
                        {data?.data.tanggal_akhir_registrasi != '' && data?.data.tanggal_akhir_registrasi != null
                          ? format(new Date(data?.data.tanggal_akhir_registrasi), 'dd MMMM yyyy', { locale: IND })
                          : ''}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <CustomDetail
                      captionText='Dibuat pada tanggal'
                      boldText={
                        data?.data.created_at != '' && data?.data.created_at != null
                          ? format(new Date(data?.data.created_at), 'dd MMMM yyyy', { locale: IND })
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomDetail
                      captionText='Klasifikasi rekrutmen'
                      boldText={
                        data?.data.rekrutmenklasifikasi.rekrutmen_klasifikasi
                          ? data?.data.rekrutmenklasifikasi.rekrutmen_klasifikasi
                          : ''
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CustomDetail
                      captionText='Dilihat sebanyak'
                      boldText={`${data ? data.data.jumlah_dilihat : '0'} kali`}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='body2' letterSpacing={0} sx={{ mb: 1 }}>
                      bagikan lowongan
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FacebookShareButton url={`https://career.bankaltimtara.co.id/${router.asPath}`}>
                        <StyledIconButton>
                          <FacebookIcon sx={{ color: 'white' }} />
                        </StyledIconButton>
                      </FacebookShareButton>
                      <TwitterShareButton url={`https://career.bankaltimtara.co.id/${router.asPath}`}>
                        <StyledIconButton>
                          <TwitterIcon sx={{ color: 'white' }} />
                        </StyledIconButton>
                      </TwitterShareButton>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper elevation={3} sx={{ px: 4, py: 8 }}>
                      <TitikRekrutmen />
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper elevation={3} sx={{ px: 4, py: 8 }}>
                      <TahapanRekrutmen />
                    </Paper>
                  </Grid>
                  {/* {!hiddenSm ? (
                    <Grid item xs={12}>
                      <Typography variant='body2' letterSpacing={0} sx={{ mb: 1 }}>
                        unduh dokumen
                      </Typography>
                      <Typography
                        variant='body1'
                        fontWeight={600}
                        letterSpacing={0}
                        sx={{ mb: 4, color: theme => theme.myColor?.colorToggle }}
                      >
                        format surat pernyataan tidak pernah diberhentikan secara tidak hormat dari instansi manapun
                        (.doc)
                      </Typography>
                      <Typography
                        variant='body1'
                        fontWeight={600}
                        letterSpacing={0}
                        sx={{ mb: 4, color: theme => theme.myColor?.colorToggle }}
                      >
                        format surat pernyataan asli belum menikah dan bersedia tidak menikah selama mas trainee (.doc)
                      </Typography>
                    </Grid>
                  ) : null} */}
                </Grid>
              </Grid>
              <Grid item xs={12} sm={7} sx={{ px: 2, mb: 8 }}>
                <Paper elevation={3} sx={{ px: 8, py: 12 }}>
                  <Typography
                    variant='h4'
                    fontWeight={600}
                    letterSpacing={0}
                    sx={{ mb: 6, color: theme => theme.myColor?.colorToggle }}
                  >
                    {data?.data.sub_judul}
                  </Typography>
                  <Box
                    sx={{
                      mb: 2,
                      position: 'relative',
                      '& .ql-toolbar, .ql-container': {
                        border: 'none',
                        fontFamily:
                          '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"'
                      },
                      '& :hover.ql-toolbar, & :hover.ql-container': {
                        // border: theme => `1px solid ${theme.myColor?.borderHoverColor} !important`,
                        // borderRadius: 1
                      },
                      '& .ql-toolbar': {
                        position: 'relative',
                        mb: 2,
                        py: 3.6,
                        height: '56px'
                      },
                      '& .ql-toolbar:before': {
                        content: '"Deskripsi Rekrutmen"',
                        position: 'absolute',
                        top: -10,
                        ml: 0,
                        px: 1.5,
                        fontSize: '0.75rem',
                        color: theme => theme.palette.text.secondary,
                        backgroundColor: theme => theme.palette.background.paper,
                        width: 'fit-content',
                        zIndex: 99
                      },
                      '& .ql-toolbar:after, & .ql-container:after': {
                        content: '""',
                        width: '100%',
                        height: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none'
                      },
                      '& .ql-editor': {
                        p: 0,
                        minHeight: 200,
                        fontSize: '0.875rem'
                      },
                      '& .ql-fill': {
                        fill: theme => theme.myColor?.toolbarColor
                      },
                      '& .ql-stroke': {
                        stroke: theme => theme.myColor?.toolbarColor
                      },
                      '& :focus-within.quill': {
                        '& .ql-container, & .ql-toolbar': {
                          // border: '1px solid transparent !important'
                        },
                        '& .ql-toolbar::after, & .ql-container::after': {
                          // border: theme => `2px solid ${theme.palette.primary.main} !important`,
                          // borderRadius: 1
                        },
                        '& .ql-toolbar:before': {
                          color: theme => theme.palette.primary.main
                        }
                      },
                      '& .ql-editor h1, & .ql-editor h2': {
                        color: theme => theme.myColor?.colorToggle
                      },
                      '& .ql-editor ul': {
                        pl: 0
                      },
                      '& .ql-clipboard': {
                        display: 'none'
                      }
                    }}
                  >
                    <ReactQuill
                      theme='snow'
                      readOnly
                      modules={modules}
                      value={data ? decodeURI(data.data.deskripsi) : ''}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* <Grid item xs={12} sx={{ px: 4, mb: 6 }}>
                {hiddenSm ? (
                  <Grid item xs={12}>
                    <Typography variant='body2' letterSpacing={0} sx={{ mb: 1 }}>
                      unduh dokumen
                    </Typography>
                    <Typography
                      variant='body1'
                      fontWeight={600}
                      letterSpacing={0}
                      sx={{ mb: 4, color: theme => theme.myColor?.colorToggle }}
                    >
                      format surat pernyataan tidak pernah diberhentikan secara tidak hormat dari instansi manapun
                      (.doc)
                    </Typography>
                    <Typography
                      variant='body1'
                      fontWeight={600}
                      letterSpacing={0}
                      sx={{ mb: 4, color: theme => theme.myColor?.colorToggle }}
                    >
                      format surat pernyataan asli belum menikah dan bersedia tidak menikah selama mas trainee (.doc)
                    </Typography>
                  </Grid>
                ) : null}
              </Grid> */}
            </Grid>
          </Grid>
        </Grid>
      ) :(
        <SpinnerSmall />
      ) }
    </>
  )
}

export default DetailRekrutmen
